/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * FtpEditModal — Create new FTP study form with FTP settings, study group/study/environment
 * selection, destination folder picker, and acknowledgement document templates.
 * Migrated from: lego/page/eos/transfer-media/FtpEditModal.jsx
 */

import { useState, useRef, useEffect } from 'react'
import { Formik, Field } from 'formik'
import type { FormikProps } from 'formik'
import { Button, Form } from 'react-bootstrap'
import { cloneDeep } from 'lodash'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import Loader from '../../../components/shared/Loader'
import { Select } from '../../../components/Select'
import FsiPickerModal from '../../../components/fsi-picker/FsiPickerModal'
import AcknowledgementDocSelection from './acknowledgement-doc-selection/AcknowledgementDocSelection'
import type {
  FtpContext,
  FtpContextMetadata,
  StudyConfig,
  StudyGroupOption,
  StudyOption,
  EnvironmentOption,
  SelectOption,
  DestinationFolder,
  SelectedDocTypes,
  FtpEditFormValues
} from './types'
import type { OptionType } from '../../../components/Select'
import type { SingleValue } from 'react-select'

const FtpEditModal: React.FC = () => {
  const initialFtpContext: FtpContext = {
    id: null,
    host: '',
    name: '',
    port: 990,
    implicit: true,
    remoteFolder: '',
    remoteFolderName: '',
    metadata: { user: '', password: '' },
    enabled: false,
    frequency: 1440,
    targetFolder: '',
    targetFolderName: '',
    eosMediaTransfer: true
  }
  const initialStudyConfig: StudyConfig = {
    studyGroup: null,
    study: null,
    environment: null
  }
  const defaultConfigData: FtpEditFormValues = {
    data: {
      studyName: '',
      hostAddress: '',
      port: '990',
      transferMode: 'IMPLICIT',
      studyLevelPath: '',
      username: '',
      password: '',
      destinationFolder: '',
      studyGroup: null,
      study: null,
      environment: null
    }
  }

  const context = useServiceContext()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)
  const [rootFolderId, setRootFolderId] = useState<number | null>(null)
  const [ftp, setFtp] = useState<FtpContext>(initialFtpContext)
  const [studyConfig, setStudyConfig] = useState<StudyConfig>(initialStudyConfig)
  const [transferMode, setTransferMode] = useState<'IMPLICIT' | 'EXPLICIT'>('IMPLICIT')
  const [destinationFolder, setDestinationFolder] = useState<DestinationFolder>({ id: 0, name: '' })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isCaInstance, setIsCaInstance] = useState(false)
  const [isFolderPathError, setIsFolderPathError] = useState(false)
  const [folderPathErrorMessage, setFolderPathErrorMessage] = useState('EOS_NEW_STUDY_REMOTE_FOLDER_NOT_UNIQUE_ERR_MSG')
  const [isUniqueStudyName, setIsUniqueStudyName] = useState(true)
  const [ackDocTemplateSelection, setAckDocTemplateSelection] = useState<Partial<SelectedDocTypes>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isStudyAdmin, setIsStudyAdmin] = useState(false)
  const formRef = useRef<FormikProps<FtpEditFormValues> | null>(null)
  const cache = useRef<Record<string, boolean>>({})
  const STUDY_ADMIN_ROLE = 'STUDY_ADMIN'

  useEffect(() => {
    if (context.service.auth.getRole() === STUDY_ADMIN_ROLE) {
      setIsStudyAdmin(true)
      const cSelections = context.service.checkmate.getCSelectionsParams()
      if (cSelections['com:mdsol:client_divisions']) {
        context.api.eos.getStudyGroupDetails(cSelections['com:mdsol:client_divisions']).then(
          (_data) => {
            const data = [_data] as StudyGroupOption[]
            const studyConfigData = { ...studyConfig }
            studyConfigData.studyGroup = data
            setIsCaInstance(data[0]?.mccEnabled ?? false)
            setStudyConfig(studyConfigData)
          },
          (error) => {
            context.service.toast.error(error?.body?.message ?? 'ERROR')
          }
        )
      }
    } else {
      getStudyGroups()
    }
    context.api.eos.getEosFolderId().then(
      (_data) => {
        const data = _data as number
        setRootFolderId(data)
        context.api.files.getDetails(data).then(
          (_fileData) => {
            const fileData = _fileData as { id: number; name: string }
            const folderData: DestinationFolder = { id: fileData?.id, name: fileData?.name }
            const ftpData = { ...ftp }
            ftpData.targetFolder = fileData?.id
            setFtp(ftpData)
            setDestinationFolder(folderData)
          },
          (error: { data?: { message?: string } }) => {
            context.service.toast.error(error?.data?.message ?? 'ERROR')
          }
        )
      },
      (error: { data?: { message?: string } }) => {
        context.service.toast.error(error?.data?.message ?? 'ERROR')
      }
    )
  }, [])

  useEffect(() => {
    if (!document.body.classList.contains('modal-open')) {
      document.body.classList.add('modal-open')
    }
  }, [isFsiPickerOpen])

  const getStudyGroups = () => {
    return context.service.eosService!.getStudyGroups().then((_data) => {
      const data = _data as StudyGroupOption[]
      const studyConfigData = { ...studyConfig }
      studyConfigData.studyGroup = data
      setIsCaInstance(data[0]?.mccEnabled ?? false)
      setStudyConfig(studyConfigData)
    })
  }

  const handleStudyGroupChange = (option: SingleValue<OptionType>) => {
    if (option) {
      const configObject = studyConfig.studyGroup?.find((sg) => sg.uuid === option.value)
      if (configObject) {
        getStudies(configObject)
      }
    }
  }

  const getStudies = (configObject: StudyGroupOption) => {
    return context.api.eos.getStudyGroup(configObject.uuid, configObject.mccEnabled ?? false).then(
      (_data) => {
        const data = _data as { elements: StudyOption[] }
        const studiesData = data.elements
        const studyConfigData = { ...studyConfig }
        studyConfigData.study = studiesData
        setStudyConfig(studyConfigData)
      },
      (error: { data?: { message?: string } }) => {
        context.service.toast.error(error?.data?.message ?? 'ERROR')
      }
    )
  }

  const handleStudyChange = (option: SingleValue<OptionType>) => {
    if (option) {
      const studyConfigObject = studyConfig.study?.find((s) => s.uuid === option.value)
      if (studyConfigObject) {
        getEnvironments(studyConfigObject)
      }
    }
  }

  const getEnvironments = (studyConfigObject: StudyOption) => {
    return context.api.eos.getPlinthStudyEnvironments(studyConfigObject.uuid).then(
      (_data) => {
        const data = _data as EnvironmentOption[]
        const studyConfigData = { ...studyConfig }
        studyConfigData.environment = data
        setStudyConfig(studyConfigData)
      },
      (error: { data?: { message?: string } }) => {
        context.service.toast.error(error?.data?.message ?? 'ERROR')
      }
    )
  }

  const updateFtp = (doTransferAfterSave: boolean) => {
    if (ackDocTemplateSelection?.siteAckTemplateFsiId && ackDocTemplateSelection?.sponsorAckTemplateFsiId) {
      const formValues = formRef?.current?.values?.data
      if (!formValues) return

      let ftpData: FtpContext & {
        eosStudyGroupUuid?: string
        eosStudyUuid?: string
        eosStudyEnvUuid?: string | null
      } = { ...ftp }

      if (formValues.study && formValues.studyGroup) {
        ftpData = {
          ...ftp,
          eosStudyGroupUuid: formValues.studyGroup.value,
          eosStudyUuid: formValues.study.value,
          eosStudyEnvUuid: formValues.environment ? formValues.environment.value : null,
          port: parseInt(formValues.port, 10),
          targetFolder: destinationFolder.id
        }

        const requestPayload = {
          eosTemplateMapDTO: {
            eosStudyId: ackDocTemplateSelection.eosStudyId ?? 0,
            siteAckTemplateFsiId: ackDocTemplateSelection.siteAckTemplateFsiId,
            siteAckTemplateFsiName: ackDocTemplateSelection.siteAckTemplateFsiName ?? '',
            sponsorAckTemplateFsiId: ackDocTemplateSelection.sponsorAckTemplateFsiId,
            sponsorAckTemplateFsiName: ackDocTemplateSelection.sponsorAckTemplateFsiName ?? ''
          },
          ftpContextRequestDTO: cloneDeep(ftpData),
          id: null as number | null
        }

        context.api.settings
          .updateFtpContext(requestPayload as unknown as Parameters<typeof context.api.settings.updateFtpContext>[0])
          .then(
            (_data) => {
              const data = _data as { id: number }
              ftpData.id = data.id

              const studyGroupObject = studyConfig.studyGroup?.find((sg) => sg.uuid === formValues.studyGroup!.value)

              if (doTransferAfterSave) {
                context.service.toast.success(translate('EOS_NEW_MEDIA_CREATED_AND_TRANSFER_SUCCESS_MSG'))

                ftpSync(data.id, {
                  mccEnabled: studyGroupObject?.mccEnabled ?? false,
                  studyGroupUuid: studyGroupObject?.uuid ?? '',
                  studyUuid: formValues.study!.value,
                  studyEnvUuid: formValues.environment ? formValues.environment.value : undefined
                })
              } else {
                context.service.toast.success(translate('EOS_NEW_MEDIA_CREATED_SUCCESS_MSG'))
              }
              doClose()

              window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
              context.$rootScope.$emit('updateFtpIntegrations', data.id ?? 0)
            },
            (error) => {
              context.service.toast.error(translate(error?.body?.message ?? error?.data?.message ?? 'ERROR'))
            }
          )
          .finally(() => {
            setIsSaving(false)
          })
      }
    }
  }

  const ftpSync = (
    id: number,
    params: {
      mccEnabled: boolean
      studyGroupUuid: string
      studyUuid: string
      studyEnvUuid?: string
    }
  ) => {
    context.api.eos.startEosTransfer(id, params).then(
      () => {
        context.service.toast.success(translate('FTP_TRANSFER_STARTED'))
      },
      () => {
        context.service.toast.error(translate('FTP_TRANSFER_FAILED_TO_START'))
      }
    )
  }

  const isNodeSelectable = (node: { eosMediaFolder?: boolean; eosLevelValue?: string }): boolean => {
    return !node.eosMediaFolder || node.eosLevelValue === undefined
  }

  const validateStudyName = (studyName: string) => {
    if (studyName.length) {
      if (!(studyName in cache.current)) {
        context.api.settings.isFtpNameUnique(studyName).then(
          () => {
            cache.current[studyName] = true
          },
          () => {
            cache.current[studyName] = false
            setIsUniqueStudyName(false)
          }
        )
      } else {
        setIsUniqueStudyName(cache.current[studyName])
      }
    }
  }

  const validateFolderPath = (folderPath: string, rootId: number) => {
    if (folderPath?.length) {
      context.api.settings.validateFolderPath(folderPath, rootId).then(
        () => {
          /* valid */
        },
        (error: { data?: { message?: string } }) => {
          setFolderPathErrorMessage((prev) => error?.data?.message ?? prev)
          setIsFolderPathError(true)
        }
      )
    }
  }

  const handleAckDocUpdate = (data: SelectedDocTypes) => {
    setAckDocTemplateSelection(data)
  }

  const doClose = () => {
    setIsOpen(false)
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_NEW_MEDIA_MODAL_TITLE')}
      className="theme-sandman ftp-edit-modal-wrapper base-modal"
      size="md"
    >
      <Modal.Body>
        <Loader blocking={isSaving}>
          <Formik<FtpEditFormValues>
            initialValues={defaultConfigData}
            innerRef={formRef}
            onSubmit={() => {}}
            validate={(values) => {
              const errors: Record<string, string> = {}
              if (values.data.studyName === '') {
                errors.studyName = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (values.data.hostAddress === '') {
                errors.hostAddress = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (values.data.port === '') {
                errors.port = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              } else {
                const portValue = parseInt(values.data.port, 10)
                if (portValue > 65535) {
                  errors.port = 'PORT_MAX_VALUE'
                }
              }
              if (values.data.studyLevelPath === '') {
                errors.studyLevelPath = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (values.data.username === '') {
                errors.username = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (values.data.password === '') {
                errors.password = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (!values.data.studyGroup) {
                errors.studyGroup = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (!values.data.study) {
                errors.study = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (!values.data.environment && isCaInstance) {
                errors.environment = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              return errors
            }}
          >
            {({ values, setFieldValue, errors: _formErrors, touched }) => {
              // Validate returns flat keys; cast for direct access
              const errors = _formErrors as unknown as Record<string, string>
              return (
                <>
                  <div className="flex-container parent-container">
                    <label className="control-label half-width">
                      {translate('EOS_MEDIA_TRANSFER_CREATE_NEW_EOS_STUDY_NAME')}
                    </label>
                    <Field
                      className="form-control field-control half-width"
                      type="text"
                      name="data.studyName"
                      maxLength={178}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsUniqueStudyName(true)
                        validateStudyName(e.target.value)
                        const ftpData = { ...ftp }
                        ftpData.name = e.target.value
                        setFtp(ftpData)
                        setFieldValue('data.studyName', e.target.value)
                      }}
                    />
                    {errors.studyName &&
                      ((touched as Record<string, Record<string, boolean>>)?.data?.studyName || isSubmitted) && (
                        <div className="error">{translate(errors.studyName as string)}</div>
                      )}
                    {!isUniqueStudyName && <div className="error">{translate('FTP_VALIDATION_UNIQUE_NAME_ERROR')}</div>}
                  </div>
                  <label className="control-label field-wrapper parent-container">{translate('FTP_SETTINGS')}</label>
                  <div className="ftp-settings-wrapper">
                    <div className="flex-container parent-container">
                      <label className="control-label half-width">{translate('FTP_SETTINGS_HOST_ADDRESS')}</label>
                      <Field
                        className="form-control field-control half-width"
                        type="text"
                        maxLength={255}
                        name="data.hostAddress"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const ftpData = { ...ftp }
                          ftpData.host = e.target.value
                          setFtp(ftpData)
                          setFieldValue('data.hostAddress', e.target.value)
                        }}
                      />
                      {errors.hostAddress &&
                        ((touched as Record<string, Record<string, boolean>>)?.data?.hostAddress || isSubmitted) && (
                          <div className="error">{translate(errors.hostAddress as string)}</div>
                        )}
                    </div>
                    <div className="port-mode-wrapper flex-container field-wrapper parent-container">
                      <div className="port flex-container half-width">
                        <label className="control-label">{translate('FTP_FORM_LABEL_PORT')}</label>
                        <div className="port-input">
                          <Field
                            type="number"
                            name="data.port"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const ftpData = { ...ftp }
                              ftpData.port = e.target.value.length <= 5 ? Number(e.target.value) : ftp.port
                              setFtp(ftpData)
                              setFieldValue('data.port', ftpData.port.toString())
                            }}
                            className="form-control field-control w100p"
                          />
                          {errors.port &&
                            ((touched as Record<string, Record<string, boolean>>)?.data?.port || isSubmitted) && (
                              <div className="error">{translate(errors.port as string)}</div>
                            )}
                        </div>
                      </div>
                      <div className="transfer-mode-wrapper flex-container half-width">
                        <div className="flex-container">
                          <div className="transfer-mode flex-container">
                            <label className="control-label">{translate('FTPS_TRANSFER_MODE')}</label>
                            <div className="selection-wrapper">
                              <Form.Check
                                type="radio"
                                name="data.transferMode"
                                className="classic radio-selection w100p"
                                label={<div className="px-2">{translate('Implicit')}</div>}
                                checked={transferMode === 'IMPLICIT'}
                                onClick={() => {
                                  const ftpData = { ...ftp }
                                  ftpData.implicit = true
                                  setFtp(ftpData)
                                  setTransferMode('IMPLICIT')
                                  setFieldValue('data.transferMode', 'IMPLICIT')
                                }}
                                onChange={() => {}}
                              />
                              <Form.Check
                                type="radio"
                                name="data.transferMode"
                                className="classic radio-selection w100p"
                                label={<div className="px-2">{translate('Explicit')}</div>}
                                checked={transferMode === 'EXPLICIT'}
                                onClick={() => {
                                  const ftpData = { ...ftp }
                                  ftpData.implicit = false
                                  setFtp(ftpData)
                                  setTransferMode('EXPLICIT')
                                  setFieldValue('data.transferMode', 'EXPLICIT')
                                }}
                                onChange={() => {}}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-container parent-container">
                      <label className="control-label half-width">{translate('EOS_NEW_STUDY_FOLDER_PATH_LABEL')}</label>
                      <Field
                        className="form-control field-control half-width"
                        type="text"
                        maxLength={4095}
                        name="data.studyLevelPath"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setIsFolderPathError(false)
                          let hasLeadingChar = true
                          if (e.target.value.length && e.target.value[0] !== '/') {
                            hasLeadingChar = false
                          }
                          const ftpData = { ...ftp }
                          validateFolderPath(
                            hasLeadingChar ? e.target.value : '/' + e.target.value,
                            destinationFolder.id
                          )
                          ftpData.remoteFolder = hasLeadingChar ? e.target.value : '/' + e.target.value
                          setFtp(ftpData)
                          setFieldValue('data.studyLevelPath', hasLeadingChar ? e.target.value : '/' + e.target.value)
                        }}
                      />
                      {errors.studyLevelPath &&
                        ((touched as Record<string, Record<string, boolean>>)?.data?.studyLevelPath || isSubmitted) && (
                          <div className="error">{translate(errors.studyLevelPath as string)}</div>
                        )}
                      {isFolderPathError && <div className="error">{translate(folderPathErrorMessage)}</div>}
                    </div>
                    <div className="flex-container parent-container">
                      <label className="control-label half-width">{translate('FTP_FORM_LABEL_USERNAME')}</label>
                      <Field
                        className="form-control field-control half-width"
                        type="text"
                        name="data.username"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const ftpData = { ...ftp }
                          ftpData.metadata.user = e.target.value
                          setFtp(ftpData)
                          setFieldValue('data.username', e.target.value)
                        }}
                      />
                      {errors.username &&
                        ((touched as Record<string, Record<string, boolean>>)?.data?.username || isSubmitted) && (
                          <div className="error">{translate(errors.username as string)}</div>
                        )}
                    </div>
                    <div className="flex-container parent-container">
                      <label className="control-label half-width">{translate('FTP_FORM_LABEL_PASSWORD')}</label>
                      <Field
                        className="form-control field-control half-width"
                        type="password"
                        name="data.password"
                        placeholder="*******"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const ftpData = { ...ftp }
                          ftpData.metadata.password = e.target.value
                          setFtp(ftpData)
                          setFieldValue('data.password', e.target.value)
                        }}
                      />
                      {errors.password &&
                        ((touched as Record<string, Record<string, boolean>>)?.data?.password || isSubmitted) && (
                          <div className="error">{translate(errors.password as string)}</div>
                        )}
                    </div>
                    <div className="flex-container parent-container">
                      <label className="control-label half-width">
                        {translate('FTP_FORM_LABEL_RCM_DESTINATION_FOLDER')}
                      </label>
                      <Button
                        className="btn btn-chita btn-link destination-folder-link btn btn-default"
                        onClick={() => setIsFsiPickerOpen(true)}
                      >
                        {destinationFolder.name}
                      </Button>
                      {isFsiPickerOpen && (
                        <FsiPickerModal
                          modalClassName="level-one-modal fsi-picker-modal"
                          options={{
                            extendParams: {
                              onlyFolders: false,
                              onlyFoldersIncludeEosFolderTypeCheck: true
                            },
                            showRC: false,
                            showSystem: false,
                            selector: 'folder',
                            rootId: rootFolderId,
                            hidePickerLoader: true,
                            eosMediaTransferMode: true
                          }}
                          title={translate('SELECT_FOLDER')}
                          isOpen={isFsiPickerOpen}
                          onClose={() => setIsFsiPickerOpen(false)}
                          onSelect={(fsi: { id: number; name: string }) => {
                            setIsFolderPathError(false)
                            const ftpData = { ...ftp }
                            ftpData.targetFolder = fsi.id
                            setFtp(ftpData)
                            setDestinationFolder({ id: fsi.id, name: fsi.name })
                            setIsFsiPickerOpen(false)
                            validateFolderPath(values?.data?.studyLevelPath, fsi.id)
                          }}
                          isNodeSelectable={isNodeSelectable}
                          showAddNewFolderModal={!isStudyAdmin}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex-container fs12 parent-container">
                    <label className="control-label half-width">{translate('EOS_SETUP_STUDY_GROUP')}</label>
                    <Select
                      className="half-width"
                      placeholder={translate('EOS_SETUP_SELECT_STUDY_GROUP...')}
                      name="data.studyGroup"
                      options={
                        studyConfig.studyGroup
                          ? studyConfig.studyGroup.map((item) => ({
                              label: item.name,
                              value: item.uuid
                            }))
                          : []
                      }
                      isDisabled={!studyConfig.studyGroup}
                      onChange={(option) => {
                        setFieldValue('data.studyGroup', option)
                        handleStudyGroupChange(option as SingleValue<OptionType>)
                      }}
                    />
                    {errors.studyGroup &&
                      ((touched as Record<string, Record<string, boolean>>)?.data?.studyGroup || isSubmitted) && (
                        <div className="error">{translate(errors.studyGroup as string)}</div>
                      )}
                  </div>
                  <div className="flex-container fs12 parent-container">
                    <label className="control-label half-width">{translate('EOS_SETUP_STUDY')}</label>
                    <Select
                      className="half-width"
                      placeholder={translate('EOS_SETUP_SELECT_STUDY...')}
                      name="data.study"
                      options={
                        studyConfig.study
                          ? studyConfig.study.map((item) => ({
                              label: item.name,
                              value: item.uuid
                            }))
                          : []
                      }
                      isDisabled={!studyConfig.study}
                      onChange={(option) => {
                        setFieldValue('data.study', option)
                        handleStudyChange(option as SingleValue<OptionType>)
                      }}
                    />
                    {errors.study &&
                      ((touched as Record<string, Record<string, boolean>>)?.data?.study || isSubmitted) && (
                        <div className="error">{translate(errors.study as string)}</div>
                      )}
                  </div>
                  {isCaInstance && (
                    <div className="flex-container fs12 parent-container">
                      <label className="control-label half-width">{translate('EMTF_MD_ENVIRONMENT')}</label>
                      <Select
                        className="half-width"
                        placeholder={translate('SELECT_ENVIRONMENT')}
                        name="data.environment"
                        options={
                          studyConfig.environment
                            ? studyConfig.environment.map((item) => ({
                                label: item.name,
                                value: item.uuid
                              }))
                            : []
                        }
                        isDisabled={!studyConfig.environment}
                        onChange={(option) => {
                          setFieldValue('data.environment', option)
                        }}
                      />
                      {errors.environment &&
                        ((touched as Record<string, Record<string, boolean>>)?.data?.environment || isSubmitted) && (
                          <div className="error">{translate(errors.environment as string)}</div>
                        )}
                    </div>
                  )}
                  <div className="flex-container fs12 parent-container">
                    <AcknowledgementDocSelection handleAckDocUpdate={handleAckDocUpdate} contextId={0} />
                  </div>
                </>
              )
            }}
          </Formik>
        </Loader>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        <Button
          data-action="confirm"
          className="btn btn-primary text-capitalize"
          disabled={isSaving}
          onClick={() => {
            setIsSubmitted(true)
            formRef.current?.validateForm().then(() => {
              const formErrors = formRef.current?.errors
              if (formErrors && !Object.keys(formErrors).length) {
                setIsSaving(true)
                updateFtp(false)
              }
            })
          }}
        >
          {translate('EOS_NEW_MEDIA_SAVE_STUDY_BTN')}
        </Button>
        <Button
          data-action="confirm"
          className="btn btn-primary text-capitalize"
          disabled={isSaving}
          onClick={() => {
            setIsSubmitted(true)
            formRef.current?.validateForm().then(() => {
              const formErrors = formRef.current?.errors
              if (formErrors && !Object.keys(formErrors).length && isUniqueStudyName && !isFolderPathError) {
                setIsSaving(true)
                updateFtp(true)
              }
            })
          }}
        >
          {translate('EOS_NEW_MEDIA_SAVE_AND_TRANSFER_BTN')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FtpEditModal
